import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';

/**
 * Fills from the element's top edge to the bottom of the viewport (not 100vh when the
 * element sits below AppShell banners). Re-measures on resize/scroll/DOM size and when
 * `measureKey` changes (e.g. Nova presence bar toggles).
 */
export function useViewportConstrainedPanelHeight(measureKey = 0) {
  const ref = useRef(null);
  const [height, setHeight] = useState('100vh');

  const update = useCallback(() => {
    if (typeof window === 'undefined') return;
    const el = ref.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    const h = Math.max(160, window.innerHeight - top);
    setHeight(`${h}px`);
  }, []);

  useLayoutEffect(() => {
    update();
    const id = requestAnimationFrame(() => update());
    return () => cancelAnimationFrame(id);
  }, [update, measureKey]);

  useEffect(() => {
    update();
    window.addEventListener('resize', update);
    const onScroll = () => update();
    window.addEventListener('scroll', onScroll, true);
    const ro = new ResizeObserver(() => update());
    ro.observe(document.documentElement);
    if (document.body) ro.observe(document.body);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', onScroll, true);
      ro.disconnect();
    };
  }, [update, measureKey]);

  return { ref, height, update };
}
